// Angular Core
import { Injectable } from '@angular/core';
// Angular Common
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
// Services
import { CommonService } from './common.service';
import { Config } from '../config';
// Model
import {
  ApiResponse,
  Provisioning,
  GroupsApi,
  FirstLoginApi,
  DriverBody,
  DriversListItem,
  UserBody,
} from '../model/provisioning';
// Third part
import * as moment from 'moment';
import { UploadStatus } from '../model/UserForm';
import { Guid } from 'guid-typescript';

interface MappedGroups {
  [propName: string]: GroupsApi;
}

interface MappedDrivers {
  [propName: string]: Provisioning;
}

interface MappedFirstLogins {
  [propName: string]: FirstLoginApi;
}

/**
 * @TODO HANDLE ERROR...
 */
@Injectable({ providedIn: 'root' })
export class ProvisioningService {
  public static UserUploadRowClass = {
    OK: 'upload-ok',
    WARNING: 'upload-warninng',
    FAILED: 'upload-failed',
    READY: 'upload-ready',
  };
  provisioningOptions = {
    mobileColumns: ['name', 'email'],
  };

  private _provisioningDataChange = new BehaviorSubject(null);
  provisioningDataChange$ = this._provisioningDataChange.asObservable();

  groups: GroupsApi[];
  drivers: Provisioning[];
  driversFirstLogin: FirstLoginApi[];
  selectedRows: Array<any> = [];

  constructor(private http: HttpClient, private cService: CommonService) {}

  getDrivers(headers = null): Observable<Provisioning[]> {
    return this.http
      .get(`${Config.apiRoot.uri}companies/${this.cService.userProfile.fpId}/users?excludes=cdbUserProduct`, {
        headers,
      })
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          // console.log('User - API', bodyResponse);
          this.drivers = bodyResponse;
          return bodyResponse;
        })
      );
  }

  getDriversDecrypted(headers = null): Observable<Provisioning[]> {
    return this.http
      .get(
        `${Config.apiRoot.uri}v2/companies/${this.cService.userProfile.fpId}/users?decrypt=true&excludes=cdbUserProduct`,
        {
          headers,
        }
      )
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          //  console.log('User - API', bodyResponse);
          //set companyUser.user.id as companyUser.id for decrypted user
          bodyResponse.map((companyUser) => {
            companyUser.user.UserId = companyUser.id;
          });
          return bodyResponse;
        })
      );
  }

  getDriversFirstLogin(headers = null): Observable<FirstLoginApi[]> {
    return this.http
      .get(`${Config.apiRoot.uri}companies/${this.cService.userProfile.fpId}/loginStat`, {
        headers,
      })
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          // console.log('Driver first login - API', bodyResponse);
          this.driversFirstLogin = bodyResponse;
          return bodyResponse;
        })
      );
  }

  getGroups(headers = null): Observable<GroupsApi[]> {
    return this.http
      .get(`${Config.apiRoot.uri}companies/${this.cService.userProfile.fpId}/groups`, {
        headers,
      })
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          // console.log('Groups - API', bodyResponse);
          this.groups = bodyResponse;
          return bodyResponse;
        })
      );
  }

  /**
   * @TODO better error handler
   */
  saveUser(body: DriverBody, headers = null): Observable<ApiResponse> {
    return this.http
      .post(`${Config.apiRoot.uri}companies/${this.cService.userProfile.fpId}/users`, body, {
        headers,
      })
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          if (statusCode == 201) {
            bodyResponse = this._createSucessEntry(body.guid, bodyResponse);
          } else if (statusCode == 500) {
            bodyResponse = this._createGeneralServerError(body.guid);
          } else {
            if (!bodyResponse) {
              bodyResponse = this._createGeneralServerError(body.guid);
            } else {
              bodyResponse = this._createSpecificServerError(body.guid, bodyResponse, statusCode);
            }
          }
          // console.log(bodyResponse)
          return { statusCode, bodyResponse };
        })
      );
  }

  private _createSpecificServerError(guid: Guid, bodyResponse: any, statusCode: number): any {
    const bodyParsed = JSON.parse(bodyResponse);
    const obj = {} as any;
    obj.guid = guid;
    obj.errorCode = bodyParsed.errorCode ?? bodyParsed.error;
    obj.statusCode = statusCode;
    return JSON.stringify(obj);
  }

  private _createGeneralServerError(guid: Guid) {
    const obj = {} as any;
    obj.guid = guid;
    obj.errorCode = 'UnknownServerError';
    obj.statusCode = 500;
    const bodyResponse = JSON.stringify(obj);
    return bodyResponse;
  }

  private _createSucessEntry(guid: Guid, bodyResponse: any) {
    const obj = {} as any;
    obj.guid = guid;
    obj.statusCode = 200;
    bodyResponse = JSON.stringify(obj);
    return bodyResponse;
  }

  /**
   * @TODO better error handler
   */
  _updateUserData(
    { idUser, FirstName, MiddleName, LastName, RealStaffNumber, PhoneNumber, Email }: UserBody,
    headers = null,
    guid
  ): Observable<ApiResponse> {
    return this.http
      .patch(
        `${Config.apiRoot.uri}v2/users/${idUser}/update`,
        {
          FirstName,
          MiddleName,
          LastName,
          RealStaffNumber,
          PhoneNumber,
          Email,
        },
        {
          headers,
        }
      )
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          if (statusCode == 200) {
            bodyResponse = this._createSucessEntry(guid, bodyResponse);
          } else if (statusCode == 500) {
            bodyResponse = this._createGeneralServerError(guid);
          } else {
            if (!bodyResponse) {
              bodyResponse = this._createGeneralServerError(guid);
            } else {
              bodyResponse = this._createSpecificServerError(guid, bodyResponse, statusCode);
            }
          }
          return { statusCode, bodyResponse };
        })
      );
  }

  getStationAssignmentsForSingleUser(
    userId: number,
    start: string = null,
    end: string = null,
    headers = null
  ): Observable<ApiResponse> {
    return this.http
      .get(`${Config.apiRoot.uri}stationassignments/users/${userId}?start_date=${start}&end_date=${end}`, {
        headers,
      })
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          return { statusCode, bodyResponse };
        })
      );
  }

  getStationAssignmentsForUsers(
    userIds: number[],
    start: string = null,
    end: string = null,
    headers = null
  ): Observable<ApiResponse> {
    return this.http
      .post(
        `${Config.apiRoot.uri}stationassignments/?start_date=${start}&end_date=${end}`,
        { userIds: userIds },
        {
          headers,
        }
      )
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          return { statusCode, bodyResponse };
        })
      );
  }

  deleteStationAssignment(userId: number, id: number, headers = null): Observable<ApiResponse> {
    return this.http
      .delete(`${Config.apiRoot.uri}stationassignments/users/${userId}/${id}`, {
        headers,
      })
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          return { statusCode, bodyResponse };
        })
      );
  }

  assignStation(userId: number, groupId: number, assignDate: string, headers = null): Observable<ApiResponse> {
    headers['vrmCustom_requestType'] = 'assign-station';
    return this.http
      .put(
        `${Config.apiRoot.uri}stationassignments/users/${userId}`,
        {
          local_date_from: moment(assignDate).format('YYYY-MM-DD'),
          user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          group_id: groupId,
          company_id: this.cService.userProfile.fpId,
          created_by: this.cService.userProfile.userId,
        },
        {
          headers,
        }
      )
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          console.log(bodyResponse);
          return { statusCode, bodyResponse };
        })
      );
  }

  /**
   * @TODO better error handler
   */
  _updateUserGroups(
    { id, isManager, isDriver, driverGroupId, managerGroupIds }: DriverBody,
    headers = null
  ): Observable<ApiResponse> {
    //console.log('_updateUserGroups');
    console.log({
      isManager,
      managerGroupIds,
      isDriver,
      driverGroupId,
    });
    return this.http
      .patch(
        `${Config.apiRoot.uri}companies/${this.cService.userProfile.fpId}/users/${id}`,
        {
          isManager,
          managerGroupIds,
          isDriver,
          driverGroupId,
        },
        {
          headers,
        }
      )
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          if (typeof bodyResponse != 'object') {
            try {
              bodyResponse = JSON.parse(bodyResponse);
            } catch (ex) {
              console.log(ex);
              bodyResponse = { Error: { Message: 'Server Error. Please retry.' } };
            }
          }
          return { statusCode, bodyResponse };
        })
      );
  }

  updateUser(body: UserBody, headers = null, guid): Observable<ApiResponse> {
    return this._updateUserData(body, headers, guid);
  }

  updateUserGroup(body: DriverBody, headers = null): Observable<ApiResponse> {
    return this._updateUserGroups(body, headers);
  }

  deleteUser(userId: number, companyUserId: number, headers = null): Observable<ApiResponse> {
    return this.http
      .delete(`${Config.apiRoot.uri}v2/companies/${this.cService.userProfile.fpId}/users/${userId}`, {
        headers,
      })
      .pipe(
        map(({ bodyResponse, statusCode, headerLine }: ApiResponse) => {
          return { statusCode, bodyResponse };
        })
      );
  }

  mapGroups(headers = null): Observable<MappedGroups> {
    return this.getGroups(headers).pipe(
      map((groups: GroupsApi[]): MappedGroups => {
        return this.cService.arrayToKeyedObject(groups, 'id');
      })
    );
  }

  _populateManagers(managerGroupIds: number[], groups: MappedGroups): string[] {
    return managerGroupIds.map((groupId: number): string => {
      return groups[`${groupId}`].name;
    });
  }

  /**
   * Send activation mail
   * @todo Error handler
   * @param email: string the mail who send mail
   */
  sendActivationMail(userId: number): Observable<ApiResponse> {
    return this.http.get(`${Config.apiRoot.uri}users/send_activation/${userId}`).pipe(
      map(({ bodyResponse, statusCode }: ApiResponse) => {
        const element = { id: userId };
        return { statusCode, bodyResponse, element };
      })
    );
  }

  sendBatchActivationEmailsToAllNewUsers(users: DriversListItem[], headers = null): Observable<ApiResponse[]> {
    const fork: Observable<ApiResponse>[] = [];
    for (let i = 0; i < users.length; i++) {
      if (!users[i].firstLogin && (users[i].isManager || users[i].isDriver)) {
        fork.push(this.sendActivationMail(users[i].id));
      }
    }
    return forkJoin(fork);
  }

  allowedDomains(): Observable<ApiResponse> {
    return this.http.get(`${Config.apiRoot.uri}companies/allowed_domains`).pipe(
      map(({ bodyResponse, statusCode }: ApiResponse) => {
        return { statusCode, bodyResponse };
      })
    );
  }

  getDriversData(headers = null): Observable<DriversListItem[]> {
    return forkJoin({
      drivers: this.getDriversDecrypted(headers),
      groups: this.mapGroups(headers),
    }).pipe(
      map(({ drivers, groups }): Array<DriversListItem> => {
        drivers = drivers.filter((d) => {
          //fitter out drivers with below condition
          let ids = d.managerGroupIds.filter((id) => groups[`${id}`]);
          return ids.length == d.managerGroupIds.length;
        });

        let to_return = drivers.map((driver: Provisioning): DriversListItem => {
          return {
            isBillingAdmin: driver.isBillingAdmin,
            userName: driver.user.UserName,
            idProvisioning: driver.id,
            id: driver.user.UserId,
            firstName: driver.user.FirstName || null,
            middleName: driver.user.MiddleName || null,
            lastName: driver.user.LastName || null,
            name: driver.user.FirstName + ' ' + driver.user.LastName,
            email: driver.user.Email,
            realStaffNumber: driver.user.RealStaffNumber || null,
            phoneNumber: driver.user ? driver.user.PhoneNumber : null,
            station: null,
            manages: [],
            isDriver: driver.isDriver,
            isManager: driver.isManager,
            updatedAt: driver.updatedAt,
            createdAt: driver.createdAt,
            driverGroupId: driver.isDriver ? driver.driverGroupId : null,
            managerGroupIds: driver.isManager ? driver.managerGroupIds : [],
            firstLogin: driver.user.FirstLogin
              ? moment(driver.user.FirstLogin).format(Config.momentDateFormatUs)
              : null,
            lastLogin: driver.user.LastLogin ? moment(driver.user.LastLogin).format(Config.momentDateFormatUs) : null,
            columnsToResize: ['manages'],
          };
        });
        this.setUserStations(to_return, groups);
        return to_return;
      })
    );
  }

  private setUserStations(to_return: DriversListItem[], groups: MappedGroups) {
    for (let i = 0; i < to_return.length; i++) {
      let driver = to_return[i];
      try {
        if (driver.isDriver) {
          if (groups[`${driver.driverGroupId}`]) {
            driver.station = driver.driverGroupId ? groups[`${driver.driverGroupId}`].name : 'UNASSIGNED';
          } else {
            driver.station = 'UNASSIGNED';
          }
        }
        if (driver.isManager) {
          driver.manages = driver.managerGroupIds.length ? this._populateManagers(driver.managerGroupIds, groups) : [];
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  /**
   * Create an array of Observable and perform a fork Join of api calls.
   * @TODO : A bulk process api should be required to make the upload o users
   * @param userForms UserForm Array
   */
  forkCreateUserCalls(userForms: any[], headers = null): Observable<ApiResponse[]> {
    const fork: Observable<ApiResponse>[] = [];
    for (let i = 0; i < userForms.length; i++) {
      if (userForms[i].getUserForm.valid) {
        const { value } = userForms[i].getUserForm;
        if (value.uploadStatus != UploadStatus.AlreadyExists && value.uploadStatus != UploadStatus.Success) {
          fork.push(
            this.saveUser(
              {
                guid: value.guid,
                isDriver: value.isDriver,
                isManager: value.isManager,
                driverGroupId: value.driverGroupId,
                managerGroupIds: value.managerGroupIds,
                user: {
                  FirstName: value.firstName,
                  MiddleName: value.middleName,
                  LastName: value.lastName,
                  Email: value.email,
                  UserName: value.email,
                  RealStaffNumber: value.realStaffNumber ?? null,
                  PhoneNumber: value.phoneNumber,
                  Products: [{ ProductCode: 'MENTOR_AMZL' }, { ProductCode: 'VRM-OMS-US-EN-0001' }],
                },
              },
              headers
            )
          );
        }
      }
    }
    return forkJoin(fork);
  }

  // rxjs Event
  /**
   * Trigger the fwtch of data
   */
  triggerBehaviourprovisioningDataChange() {
    this._provisioningDataChange.next(null);
  }
}
